import { useState } from 'react';

import {
	useSelectedStore,
	useSelectedStoreInfo,
	useUserContext,
} from 'contexts';
import { useFeatureToggle, useSafeFieldValues } from 'hooks';
import { useSelectStoreOpenGTM } from 'hooks/gtm';
import type { Product } from 'models/product';
import { cn } from 'utils/classNames';

import { useStockContext } from '../ProductDetailsContext';

import DeliveryMethodPopover from './DeliveryMethodPopover';
import FindBundleProductPopover from './FindBundleProductPopover';
import LoadingStockBalanceListRowSkeleton from './LoadingStockBalanceListRowSkeleton';
import StockBalanceListRow from './StockBalanceListRow';
import StockStoreSelectorPopover from './StockStoreSelectorPopover';
import WatchableVariantPopover from './WatchableVariantPopover';

interface Props {
	className?: string;
	product: Product;
}

export default function StockBalanceList({ className, product }: Props) {
	const { storesEnabled } = useFeatureToggle();
	const { isLoading: isLoadingUserInfo } = useUserContext();
	const { selectedStore, updateSelectedStore } = useSelectedStore();
	const { isLoading: isLoadingStoreInfo, storeInfo } = useSelectedStoreInfo();
	const { sendSelectStoreOpenEvent } = useSelectStoreOpenGTM();

	const {
		allStoresStock,
		isLoadingMainVariantStock,
		isLoadingStoresStock,
		loadStoresStock,
		mainVariantStock,
		nearbyStoresStock,
	} = useStockContext();

	const [isWatchablePopoverOpen, setIsWatchablePopoverOpen] = useState(false);
	const [isDeliveryMethodPopoverOpen, setIsDeliveryMethodPopoverOpen] =
		useState(false);
	const [storeSelectorOpen, setStoreSelectorOpen] = useState(false);
	const safeContent = useSafeFieldValues(product.content);

	const isWatchable = mainVariantStock?.webStock.isWatchableOutOfStock;
	const isBundle = product.productType === 'Bundle';

	const isLoading =
		isLoadingMainVariantStock || isLoadingStoreInfo || isLoadingUserInfo;

	if (isLoading) {
		return (
			<div
				className={cn(
					className,
					'divide-y divide-strong rounded-xs border border-strong',
				)}
			>
				<LoadingStockBalanceListRowSkeleton stockType="web" />
				{storesEnabled && (
					<LoadingStockBalanceListRowSkeleton stockType="store" />
				)}
			</div>
		);
	}

	if (!mainVariantStock) {
		return null;
	}

	const { category, storeStock, webStock } = mainVariantStock;
	const isInStockInSelectedStore = Boolean(selectedStore && storeStock.inStock);

	return (
		<div
			className={cn(
				className,
				'divide-y divide-strong rounded-xs border border-strong',
			)}
		>
			<StockBalanceListRow
				locationIcon="truck"
				header={safeContent.webStockHeading}
				buttonText={
					webStock.inStock
						? safeContent.webStockAlternativeDeliveryMethodsButtonText
						: isWatchable
							? safeContent.webStockOpenWatchableVariantPopoverButtonText
							: undefined
				}
				deliveryMethods={webStock.shippingUsps?.map((method) => ({
					title: method.name,
					description: method.description,
					price: method.price,
				}))}
				inStock={webStock.inStock}
				onClick={
					webStock.inStock
						? () => setIsDeliveryMethodPopoverOpen(true)
						: isWatchable
							? () => setIsWatchablePopoverOpen(true)
							: undefined
				}
				stockInformation={webStock.stockInformation}
				statusText={webStock.text.generic}
				infoText={webStock.deliveryInformation}
				webStockAmount={webStock.availableStockLevel}
			/>
			{isWatchable && (
				<WatchableVariantPopover
					knownCustomerText={
						safeContent.watchableVariantPopoverKnownCustomerText
					}
					popoverHeading={safeContent.watchableVariantPopoverTitle}
					unknownCustomerText={
						safeContent.watchableVariantPopoverUnknownCustomerText
					}
					productId={product.id}
					submitButtonText={safeContent.watchableVariantPopoverSubmitButtonText}
					successText={safeContent.watchableVariantPopoverSuccessText}
					isOpen={isWatchablePopoverOpen}
					onClose={() => {
						setIsWatchablePopoverOpen(false);
					}}
				/>
			)}
			<DeliveryMethodPopover
				variantId={product.id}
				isOpen={isDeliveryMethodPopoverOpen}
				onClose={() => setIsDeliveryMethodPopoverOpen(false)}
				description={safeContent.webStockDeliveryMethodPopoverDescription}
				title={safeContent.webStockDeliveryMethodPopoverTitle}
				submitButtonText={
					safeContent.webStockDeliveryMethodPopoverSubmitButtonText
				}
				noResultsText={safeContent.webStockDeliveryMethodPopoverNoResultsText}
			/>
			{storesEnabled && (
				<>
					<StockBalanceListRow
						locationIcon="store"
						header={storeInfo?.name ?? safeContent.storeStockHeading}
						openingHours={storeInfo?.todaysOpeningHours.description}
						buttonText={
							selectedStore
								? safeContent.storeStockChangeStoreButtonText
								: safeContent.storeStockSelectStoreButtonText
						}
						infoText={
							isBundle &&
							isInStockInSelectedStore &&
							storeStock.deliveryInformation ? (
								<>
									<p>{storeStock.deliveryInformation}</p>
									<FindBundleProductPopover
										className="mt-4"
										bundleId={product.id}
										title={safeContent.findProductPopoverTitle}
										triggerButtonText={
											safeContent.storeStockOpenFindBundleVariantsPopoverButtonText
										}
									/>
								</>
							) : (
								storeStock.deliveryInformation
							)
						}
						inStock={storeStock.inStock}
						onClick={() => {
							loadStoresStock();
							sendSelectStoreOpenEvent();
							setStoreSelectorOpen(true);
						}}
						shelfLocations={storeStock.locations}
						categoryColor={category.color}
						categoryName={category.name}
						articleNumber={product.id}
						stockInformation={storeStock.stockInformation}
						statusText={storeStock.text.generic}
					/>
					<StockStoreSelectorPopover
						changeStoreTitle={
							safeContent.storeStockStorePopoverChangeStoreHeading
						}
						selectStoreTitle={
							safeContent.storeStockStorePopoverSelectStoreHeading
						}
						selectedStore={selectedStore}
						allStoresStock={allStoresStock}
						nearbyStoresStock={nearbyStoresStock}
						isOpen={storeSelectorOpen}
						onUpdateSelectedStore={updateSelectedStore}
						onClose={() => setStoreSelectorOpen(false)}
						isLoading={isLoadingStoresStock}
					/>
				</>
			)}
		</div>
	);
}

StockBalanceList.displayName = 'ProductDetails_StockBalanceList';
